import { useState, useCallback } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'

// Use relative /api path in production (CloudFront routes to API Gateway)
// Use VITE_API_ENDPOINT for local development (points to local server)
const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || '/api'

/**
 * Get the current ID token for API calls
 */
async function getAuthToken() {
  try {
    const session = await fetchAuthSession()
    return session.tokens?.idToken?.toString() || null
  } catch (err) {
    console.log('No auth session available')
    return null
  }
}

/**
 * Custom hook for interacting with AWS Bedrock via our API Gateway + Lambda
 */
export function useBedrockChat() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const sendMessage = useCallback(async (messages, systemPrompt = null) => {
    if (!API_ENDPOINT) {
      // Return mock response if API not configured
      console.warn('API endpoint not configured. Using mock response.')
      return mockResponse(messages[messages.length - 1]?.content || '')
    }

    setIsLoading(true)
    setError(null)

    try {
      // Get auth token if user is authenticated
      const authToken = await getAuthToken()

      const headers = {
        'Content-Type': 'application/json',
      }

      // Add Authorization header if we have a token
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`
      }

      const response = await fetch(`${API_ENDPOINT}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages,
          systemPrompt: systemPrompt || getDefaultSystemPrompt(),
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))

        // Handle auth errors specifically
        if (response.status === 401) {
          throw new Error('Please sign in to use the chat assistant.')
        }
        if (response.status === 403) {
          throw new Error('You do not have permission to access this resource.')
        }

        throw new Error(errorData.message || `API error: ${response.status}`)
      }

      const data = await response.json()
      return data.response
    } catch (err) {
      console.error('Bedrock API Error:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const streamMessage = useCallback(async (messages, onChunk, systemPrompt = null) => {
    if (!API_ENDPOINT) {
      // Simulate streaming with mock response
      const mockText = mockResponse(messages[messages.length - 1]?.content || '')
      for (const char of mockText) {
        await new Promise(resolve => setTimeout(resolve, 20))
        onChunk(char)
      }
      return mockText
    }

    setIsLoading(true)
    setError(null)

    try {
      // Get auth token if user is authenticated
      const authToken = await getAuthToken()

      const headers = {
        'Content-Type': 'application/json',
      }

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`
      }

      const response = await fetch(`${API_ENDPOINT}/chat/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages,
          systemPrompt: systemPrompt || getDefaultSystemPrompt(),
          stream: true,
        }),
      })

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Please sign in to use the chat assistant.')
        }
        throw new Error(`API error: ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let fullResponse = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        fullResponse += chunk
        onChunk(chunk)
      }

      return fullResponse
    } catch (err) {
      console.error('Bedrock Streaming Error:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    sendMessage,
    streamMessage,
    isLoading,
    error,
    clearError: () => setError(null),
  }
}

function getDefaultSystemPrompt() {
  return `You are the Employee Concierge for Navigator | Amplify 2.0, a helpful and professional AI assistant for Vanderbilt University employees.

Your role is to:
- Assist with HR-related questions (benefits, leave policies, payroll)
- Help navigate university policies and procedures
- Provide information about campus resources and services
- Guide users to the appropriate departments or systems
- Answer questions about grants, research administration, and academic services

Always be:
- Professional yet warm and approachable
- Concise but thorough in your responses
- Helpful in directing users to the right resources
- Aware that you're representing Vanderbilt University

If you don't know something specific, acknowledge it and suggest where the user might find more information or who to contact.`
}

// Mock responses when API is not configured
function mockResponse(userMessage) {
  const lowerMessage = userMessage.toLowerCase()

  if (lowerMessage.includes('fmla') || lowerMessage.includes('leave')) {
    return "I can help you with FMLA information. Vanderbilt offers Family and Medical Leave for eligible employees. To start the process, you'll need to:\n\n1. Notify your supervisor\n2. Complete the FMLA request form in Oracle HR\n3. Provide medical certification within 15 days\n\nWould you like me to pull up the request form or explain the eligibility requirements?"
  }

  if (lowerMessage.includes('grant') || lowerMessage.includes('research')) {
    return "I've checked your research portal access. You currently have 2 active grants:\n\n• NSF Award #2345678 - Balance: $127,450\n• NIH R01 - Balance: $89,200\n\nYour NSF proposal deadline is in 14 days. Would you like me to set a reminder or help you access the proposal submission portal?"
  }

  if (lowerMessage.includes('shuttle') || lowerMessage.includes('parking') || lowerMessage.includes('campus')) {
    return "The campus shuttle runs every 15 minutes between 7 AM - 10 PM on weekdays. The nearest stop to your building is at 21st Ave & Edgehill.\n\nReal-time tracking is available via the TransLoc app. Would you like me to show you the current shuttle locations or help with parking permits?"
  }

  if (lowerMessage.includes('it') || lowerMessage.includes('service') || lowerMessage.includes('portal')) {
    return "I can help you access the IT Service Portal. Common requests include:\n\n• Password resets\n• Software installation\n• VPN access\n• Equipment requests\n\nWhich service do you need? I can create a ticket for you directly."
  }

  return "I've noted your request. As your Employee Concierge, I can assist with HR inquiries, benefits information, campus navigation, and much more.\n\nCould you provide more details about what you need help with? I'm here to help."
}

export default useBedrockChat
